(ns app.domain.group.model
  (:require #?(:clj [app.domain.group.query :as group-query])
            #?(:clj [core.util.edn :as edn])
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [core.user.model :as user-model]
            [potpuri.core :as p]))

(def process-flows
  {:permit-type/P   #{:process-flow/land-use
                      :process-flow/pre-submission
                      :process-flow/site-plan
                      :process-flow/planning-with-agreement
                      :process-flow/condominium-conversion
                      :process-flow/municipal-addressing
                      :process-flow/plan-amendment
                      :process-flow/approval-council-notice-upload
                      :process-flow/us-planning
                      :process-flow/default-P}
   :permit-type/LU  #{:process-flow/zoning
                      :process-flow/default-LU}
   :permit-type/PW  #{:process-flow/encroachment
                      :process-flow/default-PW}
   :permit-type/L   #{:process-flow/default-L}
   :permit-type/LS  #{:process-flow/default-LS}
   :permit-type/BS  #{:process-flow/default-BS}
   :permit-type/B   #{:process-flow/default-B}
   :permit-type/CE  #{:process-flow/default-CE}
   :permit-type/CR  #{:process-flow/default-CR}
   :permit-type/SW  #{:process-flow/default-B}
   :permit-type/PI  #{:process-flow/default-PI}
   :permit-type/PWO #{:process-flow/default-PWO}})

(def all-process-flow-keywords
  (->> process-flows
       vals
       (apply concat)
       set))

(def process-flow-countries
  "Process flows that are only offered in some countries. Flows not listed here are offered everywhere.

  Note that this only restricts what can be selected in the group admin; `all-process-flow-keywords`
  stays unrestricted so that the schemas and the state machine definitions remain country agnostic."
  {:process-flow/us-planning #{"us"}})

(defn process-flows-for-country [country permit-type]
  (->> (get process-flows permit-type)
       (remove (fn [process-flow]
                 (when-let [countries (process-flow-countries process-flow)]
                   (not (contains? countries country)))))
       set))


(def authority-group-child-roles
  #{:group.role/reviewer
    :group.role/review-assignment-group ; child group of reviewer group
    :group.role/archivist
    :group.role/approver
    :group.role/inspector
    :group.role/dispatcher
    :group.role/reporting
    :group.role/csr
    :group.role/cbo
    :group.role/manager
    :group.role/clerk
    :group.role/write-auth
    :group.role/viewer
    :group.role/cashier})

(def domain-group-child-roles
  #{:group.role/domain-admin
    :group.role/escrow-admin
    :group.role/property-management
    :group.role/bond-admin
    :group.role/reporting
    :group.role/meeting-coordinator})

(def superdomain-group-child-roles
  #{:group.role/reporting})

(def group-roles (set/union authority-group-child-roles
                            domain-group-child-roles
                            #{:group.role/email-recipient
                              ;; the users under a business that are allowed to use funds from the escrow account
                              :group.role/escrow-account-owner}))

(def municipal-case-id-attrs [:group/municipal-case-id-prefix
                              :group/municipal-case-id-separator
                              :group/municipal-case-id-source
                              :group/initial-case-id-counter])

(def municipal-case-id-optional-attrs [:group/municipal-case-id-template
                                       :group/municipal-case-id-counter-digits
                                       :group/municipal-case-id-rollover
                                       :group/municipal-case-id-new-number-on-renewal?
                                       :group/municipal-case-id-year-only-on-renewal?])

(def all-municipal-case-id-attrs (concat municipal-case-id-attrs municipal-case-id-optional-attrs))
(def inherit-municipal-case-id-attrs [:group.config/municipal-case-id-prefix
                                      :group.config/municipal-case-id-separator
                                      :group.config/initial-case-id-counter
                                      :group.config/municipal-case-id-new-number-on-renewal?
                                      :group.config/municipal-case-id-source])

(defn default-permit-number-attrs
  "Returns a map of default group-level permit number attributes for the given permit type,
  or nil if the permit type uses the default workspace-id source."
  [permit-type]
  (when-let [pt-defaults (case permit-type
                           :permit-type/CE {:group/municipal-case-id-prefix "CE"}
                           :permit-type/CR {:group/municipal-case-id-prefix         "C"
                                            :group/municipal-case-id-counter-digits 3}
                           :permit-type/LU {:group/municipal-case-id-prefix "LU"}
                           :permit-type/L  {:group/municipal-case-id-prefix                 "L"
                                            :group/municipal-case-id-new-number-on-renewal? true}
                           :permit-type/PW {:group/municipal-case-id-prefix                 "PW"
                                            :group/municipal-case-id-new-number-on-renewal? true}
                           :permit-type/PWO {:group/municipal-case-id-prefix "PWO"}
                           :permit-type/B {:group/municipal-case-id-prefix                 "B"
                                           :group/municipal-case-id-new-number-on-renewal? true}
                           nil)]
    (merge
      {:group/municipal-case-id-counter-digits 4
       :group/municipal-case-id-source         :case-id-source/custom-id
       :group/municipal-case-id-rollover       true
       :group/municipal-case-id-separator      "-"
       :group/municipal-case-id-template       "PSYYSN"}
      pt-defaults)))

(def prefix-dependent-municipal-case-id-attr?
  "Values that don't matter if the municipal case id prefix is not set for the group config
  i.e. meaning that the group config doesn't have a custom case id"
  (-> (set inherit-municipal-case-id-attrs)
      (conj :group.config/municipal-case-id-common-counter?)
      (disj :group.config/municipal-case-id-source)
      (disj :group.config/municipal-case-id-new-number-on-renewal?)))

(def group-features-for-group-admin
  "Features that can be configured by the group admin themselves"
  #{:group.feature/reviews-in-submitted
    :group.feature/detailed-applicant-review-history
    :group.feature/review-time-tracking
    :group.feature/inspection-time-tracking
    :group.feature/assessment-reporting
    :group.feature/assessment-reporting-automatic
    :group.feature/fee-integration
    :group.feature/generate-bill-pdf
    :group.feature/generate-bill-pdf-for-draft
    :group.feature/orders-for-inspections-enabled
    :group.feature/inspection-checklists
    :group.feature/inspection-checklist-item-note
    :group.feature/inspection-quick-pass
    :group.feature/comply-by-date
    :group.feature/target-processing-times
    :group.feature/hide-internal-notes
    :group.feature/application-inspections
    :group.feature/execution-inspections
    :group.feature/applicant-visible-inspections
    :group.feature/workspaces-visible-outside-domain-group
    :group.feature/public-map
    :group.feature/domain-public-map
    :group.feature/contact-request
    :group.feature/complaint-photos
    :group.feature/deficiency-numbering
    :group.feature/photo-numbering
    :group.feature/multiple-inspection-bookings-authority
    :group.feature/multiple-inspection-bookings-applicant
    :group.feature/bill-invoice-numbering
    :group.feature/inspection-restriction-role
    :group.feature/recurring-inspections
    :group.feature/inspection-restriction-qualification
    :group.feature/bookings-based-on-comply-by-date
    :group.feature/select-deficiency-for-booking-by-default
    :group.feature/inspection-priority
    :group.feature/deficiency-quick-pick-list-public-display
    :group.feature/condition-due-date
    :group.feature/review-due-date
    :group.feature/inspection-labels
    :group.feature/show-wizard-estimated-costs
    :group.feature/disable-ai-document-analysis
    :group.feature/disable-ai-auto-fill-for-citizens
    :group.feature/disable-plan-check
    :group.feature/disable-ai})


(def group-features-for-admin
  "Features that need to be configured by the superadmin"
  #{:group.feature/inspections
    :group.feature/fees-and-payments
    :group.feature/availability-integration
    :group.feature/property-owner-check
    :group.feature/archiving
    :group.feature/property-issue-check
    :group.feature/neighbour-properties-lookup
    :group.feature/data-import
    :group.feature/attachment-import
    :group.feature/target-processing-times
    :group.feature/backend-system-integration
    :group.feature/can-enable-hide-workspace-after-archiving?
    :group.feature/automatic-contact-integration
    :group.feature/permit-package-submission
    :group.feature/multi-factor-authentication
    :group.feature/conditions
    :group.feature/general-conditions
    :group.feature/allow-issuance-unpaid
    :group.feature/licence-notifications
    :group.feature/manual-case-ids
    :group.feature/create-property-owners-automatically
    :group.feature/create-property-owners-automatically-for-authorities
    :group.feature/admin-package
    :group.feature/public-api
    :group.feature/external-plan-review-integration
    :group.feature/ecm-integration
    :group.feature/code-report
    :group.feature/bim-ifc-checking
    :group.feature/mobile-app
    :group.feature/model-codes-integration
    :group.feature/committee-meetings
    :group.feature/periodic-inspections
    :group.feature/enable-bluebeam})

(def group-feature-global-requirements
  "Global feature flags required to enable group features. Used by the municipality-groups view and
   toggle handler. Turning a global flag off later can leave an enabled group feature in conflict
   with its requirements."
  {:group.feature/committee-meetings                #{:feature-flag.id/committee-meetings-for-b-and-ce}
   :group.feature/periodic-inspections              #{:feature-flag.id/periodic-inspections}
   :group.feature/bonds                             #{:feature-flag.id/bonds}
   :group.feature/detailed-applicant-review-history #{:feature-flag.id/review-history
                                                      :feature-flag.id/applicant-review-history}})

(def domain-group-features-for-admin
  "Domain group features that need to be configured by the Cloudpermit system admin"
  #{:group.feature/custom-branding
    :group.feature/optional-manual-property-id
    :group.feature/disable-ai
    :group.feature/escrow-accounts
    :group.feature/property-management
    :group.feature/pdfa-conversion-for-attachments
    :group.feature/public-api
    :group.feature/bonds})

(def property-data-source-values
  [:gis-backend/elasticsearch
   :gis-backend/remote
   :gis-backend/both])

(def authority-group-features-for-domain-admin
  "Authority group features that need to be configured by the domain admin"
  #{:group.feature/use-escrow-payments
    :group.feature/use-bond-accounts})

;; Please run the pseudo-migration from app.domain.group.assert-features after changing
;; either sub-feature->main-feature or permit-types-for-feature to ensure that no existing
;; groups have a disallowed combination set in the database
(def sub-feature->main-feature
  "Mapping from sub-feature to the main feature that needs to be enabled."
  {:group.feature/inspection-checklists                                :group.feature/inspections
   :group.feature/inspection-checklist-item-note                       :group.feature/inspection-checklists
   :group.feature/inspection-time-tracking                             :group.feature/inspections
   :group.feature/inspection-quick-pass                                :group.feature/inspections
   :group.feature/comply-by-date                                       :group.feature/inspections
   :group.feature/orders-for-inspections-enabled                       :group.feature/inspections
   :group.feature/application-inspections                              :group.feature/inspections
   :group.feature/execution-inspections                                :group.feature/inspections
   :group.feature/applicant-visible-inspections                        :group.feature/inspections
   :group.feature/mobile-app                                           :group.feature/inspections
   :group.feature/inspection-priority                                  :group.feature/inspections
   :group.feature/fee-integration                                      :group.feature/fees-and-payments
   :group.feature/show-wizard-estimated-costs                          :group.feature/fees-and-payments
   :group.feature/general-conditions                                   :group.feature/conditions
   :group.feature/create-property-owners-automatically-for-authorities :group.feature/property-owner-check
   :group.feature/create-property-owners-automatically                 :group.feature/create-property-owners-automatically-for-authorities
   :group.feature/external-plan-review-integration                     :group.feature/public-api
   :group.feature/recurring-inspections                                :group.feature/inspections
   :group.feature/availability-integration                             :group.feature/inspections
   :group.feature/automatic-contact-integration                        :group.feature/backend-system-integration
   :group.feature/property-issue-check                                 :group.feature/backend-system-integration
   :group.feature/ecm-integration                                      :group.feature/admin-package
   :group.feature/periodic-inspections                                 :group.feature/inspections
   :group.feature/deficiency-quick-pick-list-public-display            :group.feature/inspections
   :group.feature/condition-due-date                                   :group.feature/conditions
   :group.feature/inspection-labels                                    :group.feature/inspections})


(def main-feature->sub-features
  "Mapping from main-feature to sub-features (and their sub-features recursively)"
  (let [main->subs (reduce-kv
                     (fn [acc sub-feature main-feature]
                       (update acc main-feature (fnil conj #{}) sub-feature))
                     {}
                     sub-feature->main-feature)]
    (letfn [(expand [sub-features]
              (->> sub-features
                   (mapcat (fn [sub-feature]
                             (concat [sub-feature]
                                     (some->> (get main->subs sub-feature) expand))))))]
      (update-vals main->subs (comp set expand)))))

(def permit-types-for-feature
  {:group.feature/allow-issuance-unpaid            #{:permit-type/B :permit-type/L :permit-type/CR :permit-type/SW}
   :group.feature/licence-notifications            ptc-model/permit-types-with-renewal
   :group.feature/manual-case-ids                  #{:permit-type/P :permit-type/L}
   :group.feature/admin-package                    #{:permit-type/B :permit-type/CE :permit-type/P :permit-type/LU :permit-type/SW :permit-type/CR :permit-type/L :permit-type/LS :permit-type/PW :permit-type/PWO}
   :group.feature/inspections                      ptc-model/permit-types-with-inspections
   :group.feature/recurring-inspections            ptc-model/permit-types-with-inspections
   :group.feature/property-owner-check             ptc-model/permit-types-with-properties
   :group.feature/property-issue-check             ptc-model/permit-types-with-properties
   :group.feature/neighbour-properties-lookup      #{:permit-type/P :permit-type/LU :permit-type/PWO}
   :group.feature/public-map                       #{:permit-type/CE :permit-type/PWO}
   :group.feature/conditions                       #{:permit-type/P :permit-type/LU :permit-type/PWO}
   :group.feature/general-conditions               #{:permit-type/P :permit-type/LU}
   :group.feature/external-plan-review-integration ptc-model/permit-types-with-reviews
   :group.feature/enable-bluebeam                  ptc-model/permit-types-with-reviews
   :group.feature/permit-package-submission        #{:permit-type/B}
   :group.feature/target-processing-times          #{:permit-type/B :permit-type/P :permit-type/LU :permit-type/SW :permit-type/PW}
   :group.feature/data-import                      ptc-model/permit-types-with-data-import
   :group.feature/attachment-import                ptc-model/permit-types-with-data-import
   :group.feature/bim-ifc-checking                 #{:permit-type/B}
   :group.feature/committee-meetings               #{:permit-type/B :permit-type/CE}
   :group.feature/fees-and-payments                ptc-model/permit-types-with-fees-and-payments
   :group.feature/show-wizard-estimated-costs      ptc-model/permit-types-with-fees-and-payments
   :group.feature/inspection-priority              ptc-model/permit-types-with-inspections})

(def countries-for-feature
  {:group.feature/backend-system-integration #{:CA}
   :group.feature/permit-package-submission  #{:CA}})

(def mobile-app-safety-conditions
  #{:feature-flag.id/checklist-configuration-v2-mobile
    :feature-flag.id/inspection-visit-configuration-mobile})


(def is-group? (partial eu/is-type? :entity.type/group))


(defn has-feature? [group feature]
  (-> group :group/features set (contains? feature)))


(defn scopes [authority-group]
  (let [has-pi-feature?   (has-feature? authority-group :group.feature/periodic-inspections)
        filter-by-feature #(if (-> % :scope/permit-type (= :permit-type/PI)) has-pi-feature? true)]
    (->> authority-group
         :group/scopes
         (filter filter-by-feature)
         set)))


(defn scope-municipalities [authority-group]
  (->> (scopes authority-group)
       (keep :scope/municipality)
       distinct))


(defn has-multiple-municipalities? [group]
  (->> (scope-municipalities group)
       (keep :db/id)
       distinct
       count
       (< 1)))


(def group-feature-set (set/union group-features-for-group-admin
                                  group-features-for-admin
                                  domain-group-features-for-admin
                                  authority-group-features-for-domain-admin))


(defn has-role? [role group]
  (-> group :group/roles set (contains? role)))

(defn has-one-of-roles? [roles group]
  (-> roles
      set
      (some (:group/roles group))
      boolean))

(def default-timezone "America/Toronto")


(defn get-timezones [groups]
  (or (->> groups
           (keep :group/tz)
           distinct
           seq)
      [default-timezone]))

(defn get-timezone [group]
  (:group/tz group default-timezone))

(defn entities-of-group
  "Get entities that has type `entity-type` and links
  to given group via `:domain/authority-group`"
  [authority-group entity-type]
  #?(:clj  ((group-query/authority-group-linked-entities-fn entity-type) authority-group)
     :cljs (->> authority-group
                :domain/_authority-group
                (filter (eu/is-type-fn entity-type)))))

(defn entities-of-group-fn [entity-type]
  #?(:clj  (group-query/authority-group-linked-entities-fn entity-type)
     :cljs (fn [authority-group]
             (->> authority-group
                  :domain/_authority-group
                  (filter (eu/is-type-fn entity-type))))))

(defn first-of-group
  "Get first entity that has type `entity-type` and links
  to given group via `:domain/authority-group`"
  [authority-group entity-type]
  #?(:clj  (group-query/authority-group-first-entity entity-type authority-group)
     :cljs (first (entities-of-group authority-group entity-type))))


(def assessment-features #{:group.feature/assessment-reporting
                           :group.feature/assessment-reporting-automatic})

(def assessment-partners #{"MPACV2"})

(def assessment-reporting-min-date 1)
(def assessment-reporting-max-date 28)
(def assessment-reporting-default-date 12)

(defn valid-assessment-reporting-date?
  [day-of-month]
  (<= assessment-reporting-min-date day-of-month assessment-reporting-max-date))

(defn send-assessment-immediately?
  [group]
  (-> group :group/assessment-reporting-day-of-month nil?))

(defn partner-supports-monthly-reports?
  [partner]
  (= "MPACV2" partner))

(defn permit-package-upload-supported?
  [{:group/keys [assessment-reporting-partner]}]
  (= "MPACV2" assessment-reporting-partner))

(def authority-group-types #{:group.type/application-authority :group.type/upper-tier-authority})

(defn application-authority? [group]
  (= :group.type/application-authority (:group/type group)))

(defn upper-tier-authority? [group]
  (= :group.type/upper-tier-authority (:group/type group)))

(defn application-or-upper-tier-authority? [group]
  (contains? authority-group-types (:group/type group)))

(defn domain? [group]
  (= :group.type/domain (:group/type group)))

(defn superdomain? [group]
  (= :group.type/superdomain (:group/type group)))

(defn user-group? [group]
  (= :group.type/user-group (:group/type group)))

(defn external-module?
  "A thin group standing for a product another system implements; see
  `app.external-module.model`. Not an authority group: it has no scopes, no
  permit types and no permissions."
  [group]
  (= :group.type/external-module (:group/type group)))

(defn review-assignment-group? [group]
  (and (user-group? group)
       (has-role? :group.role/review-assignment-group group)))

(defn local-gov-domain? [group]
  (and (domain? group)
       (-> group :group/subtype (= :group.subtype/local-government))))

(defn group-domain [group]
  (let [domain-group (if (domain? group)
                       group
                       (:entity/parent group))]
    (:group/domain domain-group)))


(defn default-authority-group-name
  "The name an authority group gets by default: its domain group's name and the
  permit type's, e.g. \"Windsor, Building Department\"."
  [tr domain-group-name permit-type]
  (str/join ", " [domain-group-name (tr [(keyword "group-name" (name permit-type))])]))

(defn group-superdomain
  [group]
  (-> group
      eu/with-ancestors
      (p/find-first superdomain?)))

(defn inplace-superdomain
  "Replace any group with its superdomain if available, else returns the original group"
  [group]
  (or (group-superdomain group)
      group))

(defn superdomain-calendar?
  [group]
  (->> (and (:group/use-superdomain-calendar? group)
            (group-superdomain group))
       boolean))

;;TODO doesn't make sense that this is only dealing with auth groups in cljs but any group in clj
(defn group-roles-for-user [group user]
  #?(:cljs (->> user
                :user/authority-groups
                (filter #(-> % :db/id (= (:db/id group))))
                first
                :group-roles)
     :clj  (->> user
                :user/groups
                (filter (fn [ug] (or (du/is-descendant? group ug)
                                     (when-let [superdomain (group-superdomain group)]
                                       (du/is-descendant? superdomain ug)))))
                (mapcat :group/roles)
                set)))

(defn any-required-authority-role? [authority-group user allowed-authority-roles]
  (->> user
       (group-roles-for-user authority-group)
       (set/intersection (into #{} allowed-authority-roles))
       seq
       boolean))

(defn group-qualifications-for-user [authority-group user]
  #?(:cljs (some->> user
                    :user/authority-groups
                    (filter #(-> % :db/id (= (:db/id authority-group))))
                    first
                    :group-qualifications)
     :clj  (some->> user
                    :user/groups
                    (filter (partial du/is-descendant? authority-group))
                    (keep :group/inspector-qualification)
                    seq
                    set)))

(defmulti ^{:arglists '([entity])} get-authority-group
  "Get the authority group related to an entity.
   Entity types without a method of their own resolve through `:entity/parent`."
  (fn [{entity-type :entity/type}] entity-type))

(defmethod get-authority-group :default [entity]
  (some-> entity :entity/parent get-authority-group))

(defmethod get-authority-group :entity.type/permit-type-config [ptc]
  (:group/_permit-type-configs ptc))

(def ^{:arglists '([group])} child-groups
  "Get the immediate children of type group for an entity (a group).

  Use the `app.domain.group.core/with-children` if you need all
  children in a hierarchy."
  (child/child-entities-of-type-fn :entity.type/group))

(defn descendant-authority-groups
  "Fetch descendant authority groups for domain and superdomain groups."
  [group]
  (if (superdomain? group)
    (->> (child-groups group)
         (mapcat child-groups)
         (filter application-or-upper-tier-authority?))
    (->> (child-groups group)
         (filter application-or-upper-tier-authority?))))

(defn descendant-tenant-groups
  "Everything a tenant has: its descendant authority groups *and* its
  external-module groups. The opt-in counterpart of
  `descendant-authority-groups`, for the few places that represent the tenant
  to an admin rather than reason about authority groups."
  [group]
  (concat (descendant-authority-groups group)
          (filter external-module? (child-groups group))))

(defn sibling-authority-groups [auth-group]
  (-> auth-group
      :entity/parent
      descendant-authority-groups
      set))

(defn- domain-group-of [authority-or-domain-group]
  (when authority-or-domain-group
    (if (application-or-upper-tier-authority? authority-or-domain-group)
      (:entity/parent authority-or-domain-group)
      authority-or-domain-group)))

(defn ai-disabled?
  "True when the NoVa AI chat assistant is disabled for the group. Despite the generic
   feature name, this does not disable every AI feature — AI document analysis in the
   application wizard is controlled separately by `ai-document-analysis-disabled?`."
  [authority-or-domain-group]
  (has-feature? (domain-group-of authority-or-domain-group) :group.feature/disable-ai))

(defn ai-document-analysis-disabled? [authority-or-domain-group]
  (has-feature? (domain-group-of authority-or-domain-group) :group.feature/disable-ai-document-analysis))

(defn ai-auto-fill-disabled?
  "Checks Auto Fill settings for the release mode; authority? means authority in the target group."
  [authority-or-domain-group feature-flags authority?]
  (or (ai-document-analysis-disabled? authority-or-domain-group)
      (and (contains? feature-flags :feature-flag.id/ai-settings-controls)
           (not authority?)
           (has-feature? (domain-group-of authority-or-domain-group)
                         :group.feature/disable-ai-auto-fill-for-citizens))))

(defn plan-check-disabled?
  "Checks PlanCheck group settings for the release mode."
  [authority-or-domain-group feature-flags]
  (if (contains? feature-flags :feature-flag.id/ai-settings-controls)
    (has-feature? (domain-group-of authority-or-domain-group) :group.feature/disable-plan-check)
    (ai-document-analysis-disabled? authority-or-domain-group)))

(defn permit-type-configs
  "Returns group's permit type configs, excluding PI configs when periodic inspections are disabled."
  [group]
  (cond->> (:group/permit-type-configs group)
    (not (has-feature? group :group.feature/periodic-inspections))
    (remove #(-> % :permit-type-config/permit-type (= :permit-type/PI)))))

(defn get-permit-types [group]
  ;; Gets group's permit type, additionally checks descendant authority groups
  (->> group
       (conj (descendant-authority-groups group))
       (mapcat permit-type-configs)
       (keep :permit-type-config/permit-type)
       set))

(defn primary-permit-type [authority-group]
  (->> authority-group
       get-permit-types
       ;; Assume only one primary permit type per authority group
       (some ptc-model/primary-permit-types)))

(defn get-scope-permit-types [authority-group]
  (->> (scopes authority-group)
       (map :scope/permit-type)
       set))

(defn scopes-for-permit-type [authority-group permit-type]
  (->> (scopes authority-group)
       (filter #(= (:scope/permit-type %) permit-type))))

(defn find-descendant-group-by-permit-type [superdomain-group permit-type]
  (p/find-first (descendant-authority-groups superdomain-group)
                #(contains? (get-permit-types %) permit-type)))

(defn format-long-name
  ([group]
   (format-long-name group [(:group/name group)]))
  ([group prev-parts]
   (if-let [parent-name (when-not
                          (-> group :entity/parent superdomain?)
                          (-> group :entity/parent :group/name))]
     (format-long-name (:entity/parent group) (cons parent-name prev-parts))
     (str/join " - " prev-parts))))

(defn get-i18n-mapping
  "Extracts i18n mapping from authority-group, domain-group or other group-like map/entity,
  respects inheritance from superdomain-group"
  [group]
  (when-let [property-i18n-mapping (->> (eu/with-ancestors group)
                                        reverse
                                        (some :group/property-i18n-mapping))]
    #?(:clj  (if (string? property-i18n-mapping)
               (edn/decode-cached property-i18n-mapping)
               property-i18n-mapping)
       :cljs property-i18n-mapping)))

(defn group-tr-fn
  "Give a context-dependent tr function for a superdomain-, domain- or authority-group"
  [tr group]
  (i18n-model/wrap-tr-fn
    (merge (get-i18n-mapping group)
           ;; permit types only applicable for authority-groups
           (when-not (domain? group)
             (-> group get-permit-types i18n-model/get-permit-type-specific-tr-keys)))
    tr))

(defn csr-group [authority-group]
  (->> authority-group
       child-groups
       (filter #(has-role? :group.role/csr %))
       first))

(defn has-mobile-feature?
  "Checks main or mobile feature with respect to mobile enabled. Note args wrapped in vector for use with rf/sub"
  [[main-feature-enabled? mobile-supported? mobile-enabled?]]
  (boolean (and main-feature-enabled?
                (or (not mobile-enabled?)
                    mobile-supported?))))

(defn- main-feature-enabled?
  "Checks if group has a required main feature to be able to configure a sub feature."
  [group feature]
  (if-let [main-feature (sub-feature->main-feature feature)]
    (has-feature? group main-feature)
    true))

(def permanent-features
  "Features, which cannot be disabled once enabled"
  #{:group.feature/inspections})

(defn- sub-features-enabled?
  [{:group/keys [features]} feature]
  (if-let [sub-features (main-feature->sub-features feature)]
    (boolean (seq (set/intersection features sub-features)))
    false))

(defn disable-feature-issue
  [group feature]
  (cond
    (contains? permanent-features feature) :group.feature.issue/permanent-feature
    (sub-features-enabled? group feature) :group.feature.issue/sub-features-enabled))

(defn- enable-feature-issue
  [group enabled-global-flags feature]
  (cond
    (-> group-feature-global-requirements
        (get feature)
        (set/difference enabled-global-flags)
        seq)
    :group.feature.issue/global-feature-flag-not-enabled
    (not (main-feature-enabled? group feature))
    :group.feature.issue/main-feature-not-enabled))

(defn configure-feature-issue
  "Return a reason if some issue forbids configuring, nil otherwise."
  [group enabled-global-flags feature enabling?]
  (if enabling?
    (enable-feature-issue group enabled-global-flags feature)
    (disable-feature-issue group feature)))

(defn can-configure-feature?
  [group enabled-global-flags feature enabling?]
  (nil? (configure-feature-issue group enabled-global-flags feature enabling?)))

(defn feature-available?
  [group country feature]
  (let [countries    (get countries-for-feature feature)
        main-feature (sub-feature->main-feature feature)]
    (and (or (nil? main-feature)
             (feature-available? group country main-feature))
         (or (nil? countries)
             (contains? countries country))
         (or (domain? group)
             (if-let [valid-permit-type (get permit-types-for-feature feature)]
               (->> (get-permit-types group)
                    (some valid-permit-type))
               true)))))

(defn backend-system-name
  ([tr group-id]
   (backend-system-name tr group-id :backend-system/generic))
  ([tr group-id fallback-key]
   (tr [(keyword "backend-system" (name group-id))
        fallback-key])))

(defn use-api-keys?
  [group]
  (some (partial has-feature? group)
        [:group.feature/public-api
         :group.feature/external-plan-review-integration]))

(def email-branding-attributes [:group/branding-email-header-colour
                                :group/branding-email-closing-statement
                                :group/branding-email-disclaimer
                                :group/branding-email-logo])

(defn get-authority+domain-group [authority-or-domain-group]
  (if (application-authority? authority-or-domain-group)
    [authority-or-domain-group (:entity/parent authority-or-domain-group)]
    [nil authority-or-domain-group]))

(defn authority-group-in-subdivision? [subdivision auth-group]
  (->> (scopes auth-group)
       (map :scope/municipality)
       (some #(= (:municipality/subdivision %) subdivision))))

(defn user-groups
  "Immediate child groups of type :group.type/user-group under the given authority group."
  [authority-group]
  (->> (child-groups authority-group)
       (filter #(= :group.type/user-group (:group/type %)))))

(defn get-users-in-group
  "Return users belonging to the given group.
   - If `group` is a domain group, collects user-groups of all child authority groups.
   - If `group` is an authority group, collects its user-groups.
   - If `group` is a user-group, uses it directly.
   Service accounts are filtered out."
  [group]
  (when group
    (let [gtype (:group/type group)
          ugs   (cond
                  (= gtype :group.type/user-group) [group]
                  (domain? group) (mapcat user-groups (child-groups group))
                  :else (user-groups group))
          users (->> ugs
                     (mapcat :user/_groups)
                     distinct)]
      (remove user-model/service-account? users))))

(defn header-image-for-template
  "Returns group header image file entity with patched filename. Patch implies hashmap output."
  [group]
  (some-> group
          :group/form-pdf-header-image
          eu/->map
          (assoc :file/filename "header-image")))

(defn superdomain-domain-groups [superdomain]
  (->> superdomain
       child-groups
       (filter domain?)))

(defn group-and-superdomain-if-any [group]
  (->> [group]
       (concat (some-> group group-superdomain vector))))

(defn group-and-supergroup-child-groups [group]
  ;; NOTE: only returns *direct* child groups (via `child-groups`) of the group and its
  ;; superdomain. Grandchildren such as qualification groups or review assignment groups
  ;; (which live under the reviewer user-group) are intentionally NOT counted here.
  (->> (group-and-superdomain-if-any group)
       (mapcat child-groups)))

(defn reviewer-group
  [authority-group]
  (p/find-first (child-groups authority-group)
                #(contains? (set (:group/roles %)) :group.role/reviewer)))

(defn review-assignment-groups
  [authority-group]
  (->> (some-> (reviewer-group authority-group) child-groups)
       (filter review-assignment-group?)))

(defn authority-group-ids [group]
  (if (application-or-upper-tier-authority? group)
    #{(:db/id group)}
    (->> (descendant-authority-groups group)
         du/entity-id-set)))

(def ^:private common-templates
  "default common templates for all permit types"
  #{:entity.type/receipt-template
    :entity.type/bill-pdf-template
    :entity.type/verdict-template
    :entity.type/letter-pdf-template})

(def ^:private available-templates
  "default available templates per permit type, without additional group features"
  {:permit-type/CE  common-templates
   :permit-type/CR  (conj common-templates :entity.type/licence-revocation-letter-template)
   :permit-type/L   (conj common-templates :entity.type/licence-revocation-letter-template)
   :permit-type/B   common-templates
   :permit-type/LU  (conj common-templates :entity.type/public-notice-template)
   :permit-type/P   (into common-templates
                          [:entity.type/public-notice-template
                           :entity.type/planning-draft-report-template
                           :entity.type/planning-final-report-template])
   :permit-type/PW  common-templates
   :permit-type/SW  common-templates
   :permit-type/PWO common-templates})

(defn group-templates
  "Get available templates for the given authority group."
  [authority-group]
  (set/union (-> authority-group primary-permit-type available-templates)
             (when (has-feature? authority-group :group.feature/inspections)
               #{:entity.type/inspection-pdf-template})
             (when (has-feature? authority-group :group.feature/orders-for-inspections-enabled)
               #{:entity.type/order-template})
             (when (has-feature? authority-group :group.feature/admin-package)
               #{:entity.type/package-pdf-template})))

(def replaceable-templates
  "Templates where only one is usable, authority group cannot see multiple of them"
  #{:entity.type/planning-draft-report-template
    :entity.type/planning-final-report-template
    :entity.type/receipt-template
    :entity.type/bill-pdf-template
    :entity.type/public-notice-template
    :entity.type/licence-revocation-letter-template})

(def receipt-subtypes
  [:receipt-template.subtype/receipt
   :receipt-template.subtype/credit-note])

(defn select-template-for-replacement [templates primary-permit-type entity-type]
  (if (= :entity.type/receipt-template entity-type)
    (mapv (fn [subtype]
            (or (p/find-first templates #(and (-> % :receipt-template/subtype (= subtype))
                                              (= primary-permit-type (:template/permit-type %))))
                (p/find-first templates #(and (-> % :receipt-template/subtype (= subtype))
                                              (nil? (:template/permit-type %))))))
          receipt-subtypes)
    [(or (p/find-first templates #(= primary-permit-type (:template/permit-type %)))
         (p/find-first templates #(nil? (:template/permit-type %))))]))

(defn template-filter
  "Filter templates originated from superdomain that are applicable for the given authority group."
  [group templates]
  (let [primary-permit-type (primary-permit-type group)
        group-id            (:db/id group)]
    (-> (group-by :entity/type templates)
        (select-keys (group-templates group))
        (->> (mapcat (fn [[entity-type templates]]
                       (cond-> (filter #(or (-> % :domain/authority-group :db/id (= group-id))
                                            (nil? (:template/permit-type %))
                                            (-> % :template/permit-type (= primary-permit-type)))
                                       templates)
                         (replaceable-templates entity-type)
                         (select-template-for-replacement primary-permit-type entity-type))))
             (remove nil?)))))

(defn template-replacement [{:template/keys [replacement] :as template}]
  (or replacement template))

(defmethod i18n-model/get-localized-string-attrs :entity.type/group [_]
  [:group/branding-landing-page-titles
   :group/branding-landing-page-subtitles
   :group/branding-header-titles
   :group/complaint-display-name
   :group/public-map-info-text])
